import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as customFieldValuesApi from "@/lib/api/flow-instances/custom-field-values";
import { useInvalidateFlowCompletion } from "./use-flow-completion";

const customFieldValueKeys = {
  fieldValues: (id: string | null) => ["flowInstances", id, "field-values"] as const,
};

export function useFieldValues(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: customFieldValueKeys.fieldValues(id),
    queryFn: () => customFieldValuesApi.getFieldValues(id!),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const mutate = () =>
    queryClient.invalidateQueries({ queryKey: customFieldValueKeys.fieldValues(id) });
  return { fieldValues: data ?? null, error, isLoading, mutate };
}

/**
 * `scopeId` (one per field and goal/portfolio) runs that field's saves one
 * after another, so a later edit can never be overwritten by an earlier save
 * that commits last (QL-211).
 */
export function useSaveFieldValue(instanceId: string, scopeId?: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    scope: scopeId ? { id: scopeId } : undefined,
    mutationFn: (data: { fieldId: string; value: string; goalId?: string; portfolioId?: string }) =>
      customFieldValuesApi.saveFieldValue(instanceId, data),
    onSuccess: (result) => {
      if (result.success && result.flowFieldId) {
        queryClient.setQueryData(
          customFieldValueKeys.fieldValues(instanceId),
          (
            old: {
              values: Array<{
                flowFieldId: string;
                value: string;
                goalId: string | null;
                portfolioId: string | null;
              }>;
            } | null,
          ) => {
            const entry = {
              flowFieldId: result.flowFieldId!,
              value: result.value!,
              goalId: result.goalId ?? null,
              portfolioId: result.portfolioId ?? null,
            };
            if (!old) return { values: [entry] };
            const idx = old.values.findIndex(
              (v) =>
                v.flowFieldId === result.flowFieldId &&
                (v.goalId ?? null) === (result.goalId ?? null) &&
                (v.portfolioId ?? null) === (result.portfolioId ?? null),
            );
            const newValues = [...old.values];
            if (idx >= 0) {
              newValues[idx] = entry;
            } else {
              newValues.push(entry);
            }
            return { values: newValues };
          },
        );
        invalidateCompletion(instanceId);
      }
    },
  });
}
